import React, { useState, useRef, useEffect, useCallback } from 'react';
import { LogIn, LogOut, Camera, CheckCircle, XCircle, Shield, Users, Vibrate } from "lucide-react";
import { Html5Qrcode } from "html5-qrcode";
import { auth } from "@/lib/api";

const API = '/api';

export default function GateMensa() {
  const [user, setUser] = useState(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [tipoPasto, setTipoPasto] = useState('pranzo');
  const tipoPastoRef = useRef('pranzo');
  useEffect(() => { tipoPastoRef.current = tipoPasto; }, [tipoPasto]);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ colazione: 0, pranzo: 0, cena: 0 });
  const [lastScans, setLastScans] = useState([]);
  const html5QrRef = useRef(null);
  const autoRestartTimer = useRef(null);

  // Authenticate on mount
  useEffect(() => {
    auth.me()
      .then((userData) => {
        setUser(userData);
        setAuthenticated(true);
      })
      .catch(() => {
        window.location.href = '/login';
      });
  }, []);

  // Fetch stats periodically
  useEffect(() => {
    if (!authenticated) return;
    fetchStats();
    const interval = setInterval(fetchStats, 15000);
    return () => {
      clearInterval(interval);
      if (autoRestartTimer.current) clearTimeout(autoRestartTimer.current);
    };
  }, [authenticated]);

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => { stopScanner(); };
  }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch(API + '/mensa/stats', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setStats({
          colazione: data.colazione || 0,
          pranzo: data.pranzo || 0,
          cena: data.cena || 0
        });
      }
    } catch (e) { console.error(e); }
  };

  const startScanner = async () => {
    setError(null);
    setResult(null);
    setScanning(true);

    try {
      if (!html5QrRef.current) {
        html5QrRef.current = new Html5Qrcode('mensa-scanner');
      }
      await html5QrRef.current.start(
        { facingMode: 'environment' },
        { fps: 15, aspectRatio: 1.0 },
        onScanSuccess,
        () => {}
      );
    } catch (e) {
      setError('Errore fotocamera: ' + e.message);
      setScanning(false);
    }
  };

  const stopScanner = async () => {
    if (html5QrRef.current && html5QrRef.current.isScanning) {
      try {
        await html5QrRef.current.stop();
      } catch (e) { console.error(e); }
    }
    setScanning(false);
  };

  const vibrate = (pattern) => {
    if (navigator.vibrate) navigator.vibrate(pattern);
  };

  const onScanSuccess = async (decodedText) => {
    await stopScanner();
    vibrate([100, 50, 100]); // Double vibration on scan

    try {
      const res = await fetch(`${API}/mensa/scan?qr_code=${encodeURIComponent(decodedText)}&tipo_pasto=${tipoPastoRef.current}`, {
        method: 'POST', credentials: 'include'
      });

      if (res.ok) {
        const data = await res.json();
        vibrate(200); // Success vibration
        const scanResult = {
          success: true,
              message: tipoPastoRef.current.toUpperCase(),
          persona: data.persona_nome || decodedText,
          time: new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
        };
        setResult(scanResult);
        setLastScans(prev => [scanResult, ...prev].slice(0, 10));
        fetchStats();
      } else {
        const err = await res.json();
        vibrate([200, 100, 200, 100, 200]); // Error vibration
        setResult({ success: false, message: err.detail || 'Errore registrazione' });
      }
    } catch (e) {
      vibrate([200, 100, 200, 100, 200]);
      setResult({ success: false, message: 'Errore: ' + e.message });
    }

    // Auto-restart scanner after 3 seconds
    autoRestartTimer.current = setTimeout(() => {
      startScanner();
    }, 3000);
  };

  // Loading state
  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  // Check role
  const userRole = user?.role || 'user';
  if (userRole === 'user') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white p-4">
        <div className="text-center">
          <Shield className="h-16 w-16 mx-auto mb-4 text-red-400" />
          <h1 className="text-2xl font-bold mb-2">Accesso Negato</h1>
          <p className="text-slate-400">Non hai i permessi per accedere al Gate.</p>
          <a href="/" className="mt-4 inline-block text-blue-400 underline">Torna alla Dashboard</a>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between mb-4 px-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2"><Users className="h-6 w-6 text-orange-400"/> Gate Mensa</h1>
          <p className="text-sm text-slate-400">Scansiona badge per registrare l'accesso al pasto</p>
        </div>
        <div className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-green-400"/>
          <span className="text-sm text-slate-300">{user?.email || 'Operatore'}</span>
        </div>
      </div>

      {/* Stats counters */}
      <div className="grid grid-cols-3 gap-2 px-4">
        <div className="bg-yellow-900/30 border border-yellow-600 rounded-lg p-3 text-center">
          <div className="text-xs text-yellow-300 uppercase">Colazione</div>
          <div className="text-3xl font-bold text-yellow-200">{stats.colazione || 0}</div>
        </div>
        <div className="bg-orange-900/30 border border-orange-600 rounded-lg p-3 text-center">
          <div className="text-xs text-orange-300 uppercase">Pranzo</div>
          <div className="text-3xl font-bold text-orange-200">{stats.pranzo || 0}</div>
        </div>
        <div className="bg-indigo-900/30 border border-indigo-600 rounded-lg p-3 text-center">
          <div className="text-xs text-indigo-300 uppercase">Cena</div>
          <div className="text-3xl font-bold text-indigo-200">{stats.cena || 0}</div>
        </div>
      </div>

      {/* Scanner */}
      <div className="px-4">
        <div id="mensa-scanner" className="w-full max-w-md mx-auto bg-slate-800 rounded-lg overflow-hidden" style={{ minHeight: '280px' }}></div>
        {!scanning && !error && (
          <button onClick={startScanner} className="mt-3 mx-auto block px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center gap-2">
            <Camera className="h-4 w-4"/> Avvia scanner
          </button>
        )}
      </div>

      

      {/* Tipo Accesso Toggle - GRANDE per touch */}
      <div className="grid grid-cols-3 gap-0 rounded-lg overflow-hidden border border-slate-700">
              {["colazione", "pranzo", "cena"].map(p => (
                <button key={p} onClick={() => setTipoPasto(p)}
                  className={`py-3 text-sm font-bold uppercase tracking-wider transition-all ${tipoPasto === p ? (p === "colazione" ? "bg-yellow-500 text-white" : p === "pranzo" ? "bg-orange-500 text-white" : "bg-indigo-500 text-white") : "bg-slate-800 text-slate-400 hover:bg-slate-700"}`}>
                  {p.charAt(0).toUpperCase() + p.slice(1)}
                </button>
              ))}
            </div>

      {/* Error */}
      {error && (
        <div className="bg-red-900/50 border border-red-500 text-red-300 p-4 mx-4 mt-4 rounded-lg text-center">
          {error}
          <button onClick={startScanner} className="block mx-auto mt-2 text-blue-400 underline text-sm">
            Riprova
          </button>
        </div>
      )}

      {/* Last scans log */}
      {lastScans.length > 0 && (
        <div className="px-4 py-3">
          <div className="flex items-center gap-2 mb-2">
            <Users className="h-4 w-4 text-slate-400" />
            <span className="text-xs text-slate-400 uppercase tracking-wider">Ultime scansioni</span>
          </div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {lastScans.map((scan, i) => (
              <div key={i} className="flex items-center justify-between bg-slate-800 rounded px-3 py-2 text-sm">
                <div className="flex items-center gap-2">
                  {scan.pasto === 'colazione' ? (
                    <LogIn className="h-3 w-3 text-green-400" />
                  ) : (
                    <LogOut className="h-3 w-3 text-red-400" />
                  )}
                  <span className="text-slate-200">{scan.persona}</span>
                </div>
                <span className="text-xs text-slate-500">{scan.time}</span>
              </div>
            ))}
          </div>
        </div>
    )}
    </div>
  );
}
